
import { useState, useEffect } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, TrendingUp, TrendingDown, Calendar, Users, ThumbsUp, Crown, BarChart3 } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { SEO } from '@/components/SEO';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';

const Trends = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('weekly');
  const [hasSearched, setHasSearched] = useState(false);
  const [currentTimeFrame, setCurrentTimeFrame] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const [currentKeywordIndex, setCurrentKeywordIndex] = useState(0);
  const [isTypingPlaceholder, setIsTypingPlaceholder] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const navigate = useNavigate();

  const timeFrames = ['today', 'this week', 'this month'];

  // Sample trending keywords - updated with current trending topics
  const trendingKeywords = [
    'Iran-Israel tensions', 'AI rendering', 'multimodal AI', 'romantasy', 'Jeff Bezos wedding',
    'gold prices surge', 'missile attacks', 'AI code assistant', 'Venice controversy', 'Greece earthquake'
  ];

  // Mock poll data for search results
  const mockPolls = [
    { title: 'Will AI replace human jobs in the next 5 years?', trend: 'up', rate: '+15%' },
    { title: 'Should artificial intelligence be regulated by government?', trend: 'up', rate: '+8%' },
    { title: 'Is machine learning the future of technology?', trend: 'down', rate: '-3%' },
    { title: 'Will automation improve our daily lives?', trend: 'up', rate: '+22%' },
    { title: 'Should AI development be slowed down for safety?', trend: 'down', rate: '-12%' },
    { title: 'Is AI rendering replacing traditional design?', trend: 'up', rate: '+35%' },
    { title: 'Will multimodal AI change how we work?', trend: 'up', rate: '+18%' },
    { title: 'Should Venice ban luxury weddings?', trend: 'down', rate: '-7%' },
    { title: 'Will gold prices continue to rise amid tensions?', trend: 'up', rate: '+28%' },
  ];

  // Filter polls based on search term
  const getSearchResultPolls = () => {
    if (!searchTerm.trim()) return [];
    
    const filtered = mockPolls.filter(poll => 
      poll.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return filtered.slice(0, 5); // Show max 5 results
  };

  // Trend data for background chart (syncs with keywords)
  const keywordTrends = [
    { trend: 'up', color: '#22c55e' }, // Iran-Israel tensions - up
    { trend: 'up', color: '#22c55e' }, // AI rendering - up
    { trend: 'stable', color: '#3b82f6' }, // multimodal AI - stable
    { trend: 'up', color: '#22c55e' }, // romantasy - up
    { trend: 'down', color: '#ef4444' }, // Jeff Bezos wedding - down
    { trend: 'up', color: '#22c55e' }, // gold prices surge - up
    { trend: 'down', color: '#ef4444' }, // missile attacks - down
    { trend: 'up', color: '#22c55e' }, // AI code assistant - up
    { trend: 'stable', color: '#3b82f6' }, // Venice controversy - stable
    { trend: 'stable', color: '#3b82f6' }, // Greece earthquake - stable
  ];

  // Generate chart data based on trend direction
  const generateTrendData = (trend) => {
    const baseData = [];
    for (let i = 0; i < 20; i++) {
      let value;
      if (trend === 'up') {
        value = 30 + (i * 2) + Math.random() * 10;
      } else if (trend === 'down') {
        value = 70 - (i * 2) + Math.random() * 10;
      } else {
        value = 50 + Math.sin(i * 0.5) * 15 + Math.random() * 5;
      }
      baseData.push({ x: i, y: Math.max(10, Math.min(90, value)) });
    }
    return baseData;
  };

  // Typewriter effect for the main heading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const typewriterEffect = () => {
      const currentText = timeFrames[currentTimeFrame];
      setIsTyping(true);
      setDisplayText('');
      
      // Type out the text character by character
      for (let i = 0; i <= currentText.length; i++) {
        setTimeout(() => {
          setDisplayText(currentText.substring(0, i));
          if (i === currentText.length) {
            setIsTyping(false);
            // Wait 10 seconds before starting to type the next text
            timeoutId = setTimeout(() => {
              setCurrentTimeFrame((prev) => (prev + 1) % timeFrames.length);
            }, 10000);
          }
        }, i * 100); // 100ms delay between each character
      }
    };

    typewriterEffect();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentTimeFrame]);

  // Typewriter effect for placeholder
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const typewriterPlaceholder = () => {
      const currentKeyword = trendingKeywords[currentKeywordIndex];
      setIsTypingPlaceholder(true);
      setPlaceholderText('');
      
      // Type out the keyword character by character
      for (let i = 0; i <= currentKeyword.length; i++) {
        setTimeout(() => {
          setPlaceholderText(currentKeyword.substring(0, i));
          if (i === currentKeyword.length) {
            setIsTypingPlaceholder(false);
            // Wait 4 seconds before starting to type the next keyword
            timeoutId = setTimeout(() => {
              setCurrentKeywordIndex((prev) => (prev + 1) % trendingKeywords.length);
            }, 4000);
          }
        }, i * 80); // 80ms delay between each character
      }
    };

    typewriterPlaceholder();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentKeywordIndex]);

  // Dynamic chart data based on filter
  const getChartData = () => {
    const baseData = {
      weekly: [
        { date: 'Mon', interest: 65, votes: 1200 },
        { date: 'Tue', interest: 78, votes: 1400 },
        { date: 'Wed', interest: 82, votes: 1600 },
        { date: 'Thu', interest: 75, votes: 1350 },
        { date: 'Fri', interest: 88, votes: 1800 },
        { date: 'Sat', interest: 92, votes: 2100 },
        { date: 'Sun', interest: 85, votes: 1950 },
      ],
      monthly: [
        { date: 'Week 1', interest: 70, votes: 8500 },
        { date: 'Week 2', interest: 75, votes: 9200 },
        { date: 'Week 3', interest: 82, votes: 10100 },
        { date: 'Week 4', interest: 88, votes: 11300 },
      ],
      'all-time': [
        { date: 'Jan', interest: 65, votes: 25000 },
        { date: 'Feb', interest: 78, votes: 28000 },
        { date: 'Mar', interest: 82, votes: 32000 },
        { date: 'Apr', interest: 75, votes: 29500 },
        { date: 'May', interest: 88, votes: 35000 },
        { date: 'Jun', interest: 92, votes: 42000 },
      ]
    };
    return baseData[selectedFilter] || baseData.weekly;
  };

  // Dynamic sentiment data based on filter
  const getSentimentData = () => {
    const baseData = {
      weekly: [
        { category: 'Positive', value: 65, color: '#22c55e' },
        { category: 'Neutral', value: 25, color: '#6b7280' },
        { category: 'Negative', value: 10, color: '#ef4444' },
      ],
      monthly: [
        { category: 'Positive', value: 68, color: '#22c55e' },
        { category: 'Neutral', value: 22, color: '#6b7280' },
        { category: 'Negative', value: 10, color: '#ef4444' },
      ],
      'all-time': [
        { category: 'Positive', value: 72, color: '#22c55e' },
        { category: 'Neutral', value: 20, color: '#6b7280' },
        { category: 'Negative', value: 8, color: '#ef4444' },
      ]
    };
    return baseData[selectedFilter] || baseData.weekly;
  };

  // Dynamic search results based on filter
  const getSearchResults = () => {
    const baseData = {
      weekly: [
        { 
          keyword: 'artificial intelligence', 
          interestLevel: 'High', 
          weeklyVotes: 2400, 
          sentiment: 'Positive',
          trend: '+15%'
        },
        { 
          keyword: 'machine learning', 
          interestLevel: 'High', 
          weeklyVotes: 1800, 
          sentiment: 'Positive',
          trend: '+12%'
        },
        { 
          keyword: 'automation concerns', 
          interestLevel: 'Medium', 
          weeklyVotes: 950, 
          sentiment: 'Mixed',
          trend: '+8%'
        },
      ],
      monthly: [
        { 
          keyword: 'artificial intelligence', 
          interestLevel: 'High', 
          weeklyVotes: 9600, 
          sentiment: 'Positive',
          trend: '+22%'
        },
        { 
          keyword: 'machine learning', 
          interestLevel: 'High', 
          weeklyVotes: 7200, 
          sentiment: 'Positive',
          trend: '+18%'
        },
        { 
          keyword: 'automation concerns', 
          interestLevel: 'Medium', 
          weeklyVotes: 3800, 
          sentiment: 'Mixed',
          trend: '+14%'
        },
      ],
      'all-time': [
        { 
          keyword: 'artificial intelligence', 
          interestLevel: 'High', 
          weeklyVotes: 48000, 
          sentiment: 'Positive',
          trend: '+135%'
        },
        { 
          keyword: 'machine learning', 
          interestLevel: 'High', 
          weeklyVotes: 36000, 
          sentiment: 'Positive',
          trend: '+98%'
        },
        { 
          keyword: 'automation concerns', 
          interestLevel: 'Medium', 
          weeklyVotes: 19000, 
          sentiment: 'Mixed',
          trend: '+67%'
        },
      ]
    };
    return baseData[selectedFilter] || baseData.weekly;
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setHasSearched(true);
      setShowSearchResults(false);
    }
  };

  const handleKeywordClick = (keyword: string) => {
    setSearchTerm(keyword);
    setHasSearched(true);
    setShowSearchResults(false);
  };

  const handlePartnerClick = () => {
    navigate('/business');
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSearchResults(value.trim().length > 0);
  };

  const handleSearchResultClick = (pollTitle: string) => {
    setSearchTerm(pollTitle);
    setShowSearchResults(false);
    setHasSearched(true);
  };

  const chartConfig = {
    interest: {
      label: "Interest Level",
      color: "#8884d8",
    },
    votes: {
      label: "Total Votes",
      color: "#82ca9d",
    },
  };

  const currentTrendData = generateTrendData(keywordTrends[currentKeywordIndex].trend);
  const currentTrendColor = keywordTrends[currentKeywordIndex].color;
  const searchResultPolls = getSearchResultPolls();

  return (
    <>
      <SEO
        title="Trends - blankpoll"
        description="Discover trending topics and analyze poll data to understand what matters most to our community."
        type="website"
        imageUrl="/og-image.png"
        name="blankpoll"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="max-w-7xl mx-auto px-4 py-8 mt-16 relative">
          {/* Background Chart - Full screen width */}
          {!hasSearched && (
            <div className="absolute left-[-50vw] right-[-50vw] top-0 h-96 pointer-events-none opacity-30 overflow-hidden z-0">
              <div className="w-full h-full relative">
                <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="trendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: currentTrendColor, stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: currentTrendColor, stopOpacity: 0.1 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M ${currentTrendData.map((point, index) => 
                      `${(index / (currentTrendData.length - 1)) * 800},${400 - (point.y / 100) * 400}`
                    ).join(' L ')}`}
                    fill="none"
                    stroke={currentTrendColor}
                    strokeWidth="3"
                    opacity="0.6"
                  />
                  <path
                    d={`M ${currentTrendData.map((point, index) => 
                      `${(index / (currentTrendData.length - 1)) * 800},${400 - (point.y / 100) * 400}`
                    ).join(' L ')} L 800,400 L 0,400 Z`}
                    fill="url(#trendGradient)"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-12 relative z-10">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              What's trending{' '}
              <span className="text-primary">
                {displayText}
                {isTyping && <span className="animate-pulse">|</span>}
              </span>
              <span className="text-primary">?</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover the most discussed topics and analyze voting patterns across our platform
            </p>
          </div>

          {/* Search Section */}
          <div className="max-w-2xl mx-auto mb-12 relative z-20">
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                placeholder={placeholderText + (isTypingPlaceholder ? '|' : '')}
                value={searchTerm}
                onChange={handleSearchInputChange}
                className="pl-10 pr-20 h-12 text-lg md:text-lg text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => searchTerm.trim() && setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
              />
              <Button 
                onClick={handleSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-primary-dark"
              >
                Explore
              </Button>
              
              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {searchResultPolls.length > 0 ? (
                    <div className="py-2">
                      {searchResultPolls.map((poll, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                          onClick={() => handleSearchResultClick(poll.title)}
                        >
                          <span className="text-sm text-gray-900 dark:text-gray-100 flex-1 pr-3">
                            {poll.title}
                          </span>
                          <div className="flex items-center gap-1">
                            {poll.trend === 'up' ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            )}
                            <span className={`text-sm font-medium ${
                              poll.trend === 'up' ? 'text-green-500' : 'text-red-500'
                            }`}>
                              {poll.rate}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No results found for "{searchTerm}"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Trending Keywords */}
            {!hasSearched && (
              <div className="text-center relative z-10">
                {/*<p className="text-sm text-muted-foreground ">Popular search terms:</p>*/}
                <div className="flex flex-wrap gap-2 justify-center">
                  {trendingKeywords.map((keyword, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                      onClick={() => handleKeywordClick(keyword)}
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Results */}
          {hasSearched && (
            <>
              {/* Filters */}
              <div className="flex gap-2 mb-6 justify-center">
                {['weekly', 'monthly', 'all-time'].map((filter) => (
                  <Button
                    key={filter}
                    variant={selectedFilter === filter ? 'default' : 'outline'}
                    onClick={() => setSelectedFilter(filter)}
                    className="capitalize"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {filter.replace('-', ' ')}
                  </Button>
                ))}
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Interest Level Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Interest Level Over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer config={chartConfig} className="h-48 md:h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={getChartData()} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                          <XAxis dataKey="date" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Area 
                            type="monotone" 
                            dataKey="interest" 
                            stroke="#8884d8" 
                            fill="#8884d8" 
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>

                {/* Sentiment Analysis */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ThumbsUp className="w-5 h-5" />
                      User Sentiment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer config={chartConfig} className="h-48 md:h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getSentimentData()} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                          <XAxis dataKey="category" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="value" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Results Table */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Trending Analysis Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Keyword</TableHead>
                        <TableHead>Interest Level</TableHead>
                        <TableHead>Weekly Votes</TableHead>
                        <TableHead>Sentiment</TableHead>
                        <TableHead>Trend</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getSearchResults().map((result, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{result.keyword}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={result.interestLevel === 'High' ? 'default' : 'secondary'}
                              className={result.interestLevel === 'Medium' ? 'text-white bg-gray-500' : ''}
                            >
                              {result.interestLevel}
                            </Badge>
                          </TableCell>
                          <TableCell>{result.weeklyVotes.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={
                                result.sentiment === 'Positive' ? 'border-green-500 text-green-500' :
                                result.sentiment === 'Mixed' ? 'border-orange-500 text-orange-500' :
                                'border-red-500 text-red-500'
                              }
                            >
                              {result.sentiment}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-green-600 font-medium">{result.trend}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Partner Card */}
                  <Card className="mt-6 bg-gradient-to-r from-primary/10 to-primary-dark/10 border-primary/20">
                    <CardContent className="p-6 text-center">
                      <Crown className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Unlock Full Analytics</h3>
                      <p className="text-muted-foreground mb-4">
                        Get access to comprehensive trend analysis, advanced filtering, and detailed insights
                      </p>
                      <Button 
                        className="bg-primary hover:bg-primary-dark"
                        onClick={handlePartnerClick}
                      >
                        Become a Partner
                      </Button>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </>
          )}

          {/* Default State - Featured Trends */}
          {!hasSearched && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Most Voted This Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">15 auto gallery employees win $20M from Lotto Max</span>
                      <Badge className="text-white flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        2.4k
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Claims that Jagmeet Singh was monitored by former agent</span>
                      <Badge className="text-white flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        1.8k
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Man accused of fraud for 120 free flights as fake hostess in Florida</span>
                      <Badge className="text-white flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        1.2k
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Court rules Trump illegally deployed National Guard to L.A. protests</span>
                      <Badge className="text-white flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        0.9k
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Rising Topics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">"AI Rendering"</span>
                      <Badge variant="outline" className="text-green-600">+35%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">"Romantasy" term</span>
                      <Badge variant="outline" className="text-green-600">+75%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">"AI Code Assistant"</span>
                      <Badge variant="outline" className="text-green-600">+28%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">"Multimodal AI"</span>
                      <Badge variant="outline" className="text-green-600">+42%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">"AI Interior Design"</span>
                      <Badge variant="outline" className="text-green-600">+31%</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">Global Interests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Gold and oil prices rise after Iran-Israel tensions</span>
                      <Badge variant="secondary" className="text-white">High</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Missile attacks on Tel Aviv and Tehran; casualties reported</span>
                      <Badge variant="secondary" className="text-white">High</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Jeff Bezos' Venice wedding sparks city controversy</span>
                      <Badge variant="secondary" className="text-white">Medium</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Earthquake damage to rural monasteries in northern Greece</span>
                      <Badge variant="secondary" className="text-white">Medium</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Global budget pressures: WEF analysis on interest and spending balance</span>
                      <Badge variant="secondary" className="text-white">Low</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>

        {/* Trends Analysis CTA Section */}
        <section className="bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20 py-16 px-4 mt-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-6">
              <BarChart3 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Unlock the Power of Trend Analysis
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover emerging trends, analyze public sentiment, and make data-driven decisions with our comprehensive analytics platform. Join thousands of researchers, marketers, and decision-makers who trust our insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary-dark text-white px-8 py-3 text-lg"
                onClick={handlePartnerClick}
              >
                Start Analyzing Today
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 text-lg"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Explore Trends
              </Button>
            </div>
          </div>
        </section>

        <Footer className="mt-0" />
      </div>
    </>
  );
};

export default Trends;
